//* react
import React from 'react';

//* material-ui
import { Box, Button, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';

//* system
import { useSelector } from 'store';
import { useSnackbar } from 'notistack';
import useLocalization from 'hooks/useLocalization';
import { getTemplateDefaultFile, loadTemplateDefaultFiles } from 'store/slices/templates';
import {
  deleteTemplateDefaultFile,
  saveTemplateDefaultFile,
  uploadTemplateDefaultFile,
} from 'api/template-default-files';
import type { WhatsappTemplateHeaderFormat } from 'types/templates';

const HiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ACCEPT_BY_FORMAT: Partial<Record<WhatsappTemplateHeaderFormat, string>> = {
  IMAGE: 'image/jpeg, image/jpg, image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

type Props = {
  templateName: string;
  language: string;
  headerFormat: WhatsappTemplateHeaderFormat | null | undefined;
  disabled?: boolean;
};

/**
 * קובץ ברירת מחדל לכותרת המדיה של התבנית — נתון שלנו בלבד (לא נשלח למטא).
 * נשמר מיידית בשרת הנוד (בנפרד מהגשת התבנית לאישור), ולכן זמין גם לתבנית מאושרת.
 */
const DefaultHeaderFileSection: React.FC<Props> = ({ templateName, language, headerFormat, disabled }) => {
  const { currentLocale } = useLocalization();
  const loc = React.useMemo(
    () => currentLocale.settingsPopup.templates.createNew.defaultFile,
    [currentLocale],
  );
  const { enqueueSnackbar } = useSnackbar();
  const defaultFiles = useSelector((state) => state.templates.defaultFiles);
  const [busy, setBusy] = React.useState(false);

  const trimmedName = templateName.trim();
  const current = React.useMemo(
    () => getTemplateDefaultFile(defaultFiles, trimmedName, language),
    [defaultFiles, trimmedName, language],
  );

  if (!headerFormat || !ACCEPT_BY_FORMAT[headerFormat]) return null;

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !trimmedName) return;
    setBusy(true);
    try {
      const uploaded = await uploadTemplateDefaultFile(file);
      await saveTemplateDefaultFile({
        templateName: trimmedName,
        language,
        fileUrl: uploaded.fileUrl,
        fileName: uploaded.fileName,
      });
      await loadTemplateDefaultFiles();
      enqueueSnackbar(loc.saveSuccess, { variant: 'success' });
    } catch (error) {
      console.warn('saveTemplateDefaultFile err: ', error);
      enqueueSnackbar(loc.saveError, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!trimmedName) return;
    setBusy(true);
    try {
      //* הרשומה יכולה לשבת על שפה מפורשת או על '' (כל השפות) — מנקים את שתיהן
      await deleteTemplateDefaultFile({ templateName: trimmedName, language });
      await deleteTemplateDefaultFile({ templateName: trimmedName });
      await loadTemplateDefaultFiles();
      enqueueSnackbar(loc.removeSuccess, { variant: 'success' });
    } catch (error) {
      console.warn('deleteTemplateDefaultFile err: ', error);
      enqueueSnackbar(loc.saveError, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {loc.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {loc.description}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={!trimmedName ? loc.nameRequiredHint : ''}>
          <span>
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={disabled || busy || !trimmedName}
              startIcon={busy ? <CircularProgress size={16} /> : <AttachFileOutlined />}
            >
              {current ? loc.replaceButton : loc.uploadButton}
              <HiddenInput type="file" accept={ACCEPT_BY_FORMAT[headerFormat]} onChange={handleUpload} />
            </Button>
          </span>
        </Tooltip>
        {current && (
          <>
            <Typography variant="body2" component="span">
              {loc.currentFile}:{' '}
              <Link href={current.fileUrl} target="_blank" rel="noopener noreferrer">
                {current.fileName || current.fileUrl}
              </Link>
            </Typography>
            <Tooltip title={loc.removeButton}>
              <span>
                <IconButton size="small" color="error" disabled={disabled || busy} onClick={handleRemove}>
                  <DeleteOutlineOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DefaultHeaderFileSection;
